import React, { useState, useMemo } from 'react'
import AssessmentWrapper from '../../AssessmentWrapper'
import { MODULE_1_INSTANCES } from '../../../../data/moduleInstances'

// Updated Module 1-1 with cognitive scoring (Reading Emotions)
function Module1_1Content({ nextPage, recordScore, cardData, handleModuleComplete }) {
  const [currentQuestion, setCurrentQuestion] = useState(-1)
  const [answers, setAnswers] = useState([])
  
  // Always use all guests from MODULE_1_INSTANCES (ignore cardData selection)
  // This shows all 9+ instances available in the markdown document
  const guestData = MODULE_1_INSTANCES['1A'].guests
  
  // Function to shuffle array
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Memoize questions to prevent reshuffling on every render
  const questions = useMemo(() => {
    return guestData.map((guest, index) => {
      // Shuffle the emotions to randomize answer order (only once)
      const shuffledEmotions = shuffleArray(guest.emotions);
      // Find the new index of the correct answer after shuffling
      const correctIndex = shuffledEmotions.indexOf(guest.correct);
      
      return {
        id: index + 1,
        image: `${process.env.PUBLIC_URL}/images/characters/${guest.name}/${guest.name} ${guest.image}.png`,
        question: `What emotion is ${guest.name} expressing?`,
        options: shuffledEmotions,
        correct: correctIndex,
        guestName: guest.name,
        correctEmotion: guest.correct
      };
    });
  }, [guestData]); // Only recalculate if guestData changes

  const handleAnswer = (selectedIndex) => {
    const currentQ = questions[currentQuestion]
    const isCorrect = selectedIndex === currentQ.correct
    
    // Record the score (difficulty 2 for Module 1A)
    if (recordScore) {
      recordScore(isCorrect, {
        questionId: currentQ.id,
        selectedAnswer: selectedIndex,
        correctAnswer: currentQ.correct,
        questionType: 'emotion-recognition'
      })
    }
    
    // Store answer
    const newAnswers = [...answers]
    newAnswers[currentQuestion] = {
      questionId: currentQ.id,
      selected: selectedIndex,
      correct: currentQ.correct,
      isCorrect
    }
    setAnswers(newAnswers)
    
    // Move to next question or complete module
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1)
      }, 1000) // Delay to show score feedback
    } else {
      // Module complete
      setTimeout(() => {
        if (handleModuleComplete) {
          console.log('🎯 Module1_1Content completing via handleModuleComplete');
          handleModuleComplete({
            completedQuestions: answers.length + 1,
            totalQuestions: questions.length,
            correctAnswers: newAnswers.filter(a => a.isCorrect).length,
            allAnswers: newAnswers,
            moduleType: '1A'
          });
        } else if (nextPage) {
          console.log('🎯 Module1_1Content completing via nextPage');
          nextPage();
        }
      }, 2000)
    }
  }

  // Show directions first, then questions
  if (currentQuestion === -1) {
    return (
      <div className="assessment1-container">
        <div className="assessment1-wrap">
          <div className="assessment1-title">
            Directions - Reading Emotions
          </div>
          <div className="assessment1-content">
            As a waiter at a high-end restaurant, you need to be aware of the emotional state of various guests. Here's your chance to put your skills to the test! Choose the word or phrase that best describes the emotional state of each guest shown.
          </div>

          <div onClick={() => setCurrentQuestion(0)} className="assessment1-start-btn">
            Get started
          </div>
        </div>
      </div>
    )
  }

  const currentQ = questions[currentQuestion]

  return (
    <div className="assessment1-container enhanced">
      <div className="assessment1-wrap">
        <div className="assessment1-title">
          Reading Emotions - Question {currentQuestion + 1} of {questions.length}
        </div>
        
        <div className="question-progress">
          <div 
            className="progress-fill" 
            style={{ 
              width: `${((currentQuestion + 1) / questions.length) * 100}%` 
            }}
          />
        </div>
        
        <div className="question-image">
          <img src={currentQ.image} alt="Emotion expression" />
        </div>
        
        <div className="assessment1-content">
          {currentQ.question}
        </div>
        
        <div className="answer-options">
          {currentQ.options.map((option, index) => (
            <button 
              key={index}
              className="option-button"
              onClick={() => handleAnswer(index)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      
      <style jsx>{`
        .enhanced {
          position: relative;
        }
        
        .question-progress {
          width: 100%;
          height: 6px;
          background: #e0e0e0;
          border-radius: 3px;
          margin-bottom: 20px;
          overflow: hidden;
        }
        
        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #2196F3, #4CAF50);
          transition: width 0.5s ease;
        }
        
        .question-image {
          margin: 20px 0;
          text-align: center;
        }
        
        .question-image img {
          width: 200px;
          height: 200px;
          object-fit: cover;
          border-radius: 10px;
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        
        .answer-options {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin: 30px 0;
        }
        
        .option-button {
          padding: 15px 20px;
          font-size: 16px;
          border: 2px solid #ddd;
          border-radius: 10px;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .option-button:hover {
          border-color: #2196F3;
          background: #f3f8ff;
          transform: translateY(-2px);
        }
        
        .module-info {
          margin-top: 30px;
          padding: 15px;
          background: #f0f8ff;
          border-radius: 10px;
          font-size: 12px;
          color: #666;
        }
        
        .module-info p {
          margin: 5px 0;
        }
      `}</style>
    </div>
  )
}

// Original simple directions component for backward compatibility
function SimpleDirections(props) {
  const handleGetStarted = () => {
    // If this is wrapped in AssessmentWrapper (cognitive scoring mode),
    // complete the module instead of just going to next page
    if (props.handleModuleComplete) {
      console.log('🎯 SimpleDirections completing via handleModuleComplete (cognitive mode)');
      // In cognitive mode, this directions page counts as Module 1A completion
      props.handleModuleComplete({
        completedDirections: true,
        moduleType: '1A'
      });
    } else if (props.nextPage) {
      console.log('🎯 SimpleDirections going to next page (regular mode)');
      props.nextPage();
    }
  };

  return (
    <div className="assessment1-container">
      <div className="assessment1-wrap">
        <div className="assessment1-title">
          Directions
        </div>
        <div className="assessment1-content">
          Your love interest enters your restaurant with five other guests. As a waiter at a high-end restaurant, you know it's important to be aware of the emotional state of each guest. Here's your chance to put your skills to the test! Choose the word or phrase that best describes the emotional state of each guest.
        </div>

        <div onClick={handleGetStarted} className="assessment1-start-btn">
          Get started
        </div>
      </div>
    </div>
  )
}

// Wrapped version with cognitive scoring
const ScoredModule1_1 = (props) => (
  <AssessmentWrapper 
    moduleType="1A" 
    isAdaptive={false}
    onModuleComplete={props.onModuleComplete}
  >
    <Module1_1Content {...props} />
  </AssessmentWrapper>
)

// Export both versions for flexibility
export { SimpleDirections, ScoredModule1_1 }
export default Module1_1Content // Use the updated component with real data
